import { useState, useEffect, useRef } from "react";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { Brain, Trash } from "@phosphor-icons/react";
import { MailboxSwitcher, useMailboxApi } from "@/components/MailboxSwitcher";
import api, { errDetail } from "@/lib/api";

const STEPS = [
  ["import", "pre.step_import", "Import mail"], ["index", "pre.step_index", "Index (search)"], ["style", "pre.step_style", "Writing style"], ["relations", "pre.step_relations", "Relationships"],
];
const dot = (s) => (s === "done" ? "✓" : s === "running" ? "⟳" : "○");

export const PrelearnCard = () => {
  const { mailbox, setMailbox, mbApi } = useMailboxApi();
  const [st, setSt] = useState(null);
  const pollRef = useRef(null);

  const load = () => mbApi.get("/prelearn/status").then((r) => {
    setSt(r.data);
    if (r.data.status === "running" && !pollRef.current) {
      pollRef.current = setInterval(() => mbApi.get("/prelearn/status").then((x) => {
        setSt(x.data);
        if (x.data.status !== "running") { clearInterval(pollRef.current); pollRef.current = null; }
      }).catch(() => {}), 2500);
    }
  }).catch(() => {});
  useEffect(() => { load(); return () => pollRef.current && clearInterval(pollRef.current); }, [mailbox]); // eslint-disable-line

  const train = async () => {
    try { await mbApi.post("/prelearn/run"); toast.success(t("pre.started", "Training started")); setTimeout(load, 400); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const wipe = async () => {
    if (!window.confirm(t("pre.wipe_confirm", "Wipe all learned data (search index, writing style)? You can re-train anytime."))) return;
    try { const { data } = await mbApi.post("/prelearn/wipe"); toast.success(t("pre.wiped", "Wiped ({n} vectors)", { n: data.vectors_deleted })); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  if (!st) return null;
  const steps = st.steps || {};
  const p = st.tone_profile?.profile;
  return (
    <div data-testid="prelearn-card" className="bg-white border border-gray-200 rounded-md p-5 mb-6">
      <MailboxSwitcher mailbox={mailbox} onChange={setMailbox} />
      <div className="flex items-center gap-2 mb-1">
        <Brain size={20} className="text-purple-600" />
        <h3 className="font-heading font-bold text-gray-900">{t("pre.title", "Agent training")}</h3>
        {st.status === "done" && <span data-testid="prelearn-done-badge" className="text-[10px] font-bold bg-purple-100 text-purple-700 rounded-full px-2 py-0.5">{t("pre.trained_badge", "✓ TRAINED · {n} emails indexed", { n: st.vectors })}</span>}
      </div>
      <p data-testid="prelearn-quota" className="text-xs text-gray-500 mb-3">
        {t("pre.desc", "Your plan learns from your last {inbox} inbox + {sent} sent emails (meaning-based search, your writing style, VIP relationships).", { inbox: st.quota?.inbox, sent: st.quota?.sent })}
      </p>
      {st.status === "running" ? (
        <div data-testid="prelearn-tracker" className="space-y-1 mb-3">
          {STEPS.map(([k, tk, label]) => (
            <div key={k} data-testid={`prelearn-step-${k}`}
              className={`text-sm flex items-center gap-2 ${steps[k] === "done" ? "text-green-700" : steps[k] === "running" ? "text-purple-700 font-semibold" : "text-gray-400"}`}>
              <span className="w-4">{dot(steps[k])}</span>{t(tk, label)}
              {k === "index" && st.counts?.embedded != null && <span className="text-xs text-gray-400">{t("pre.indexed", "({n} indexed)", { n: st.counts.embedded })}</span>}
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-3 flex-wrap">
          <button data-testid="prelearn-train-btn" onClick={train}
            className="text-sm font-semibold bg-purple-600 hover:bg-purple-700 text-white rounded-md px-4 py-2 transition-colors">
            {st.status === "done" ? t("pre.retrain", "Re-train my agent") : t("pre.train", "Train my agent")}
          </button>
          {st.vectors > 0 && (
            <button data-testid="prelearn-wipe-btn" onClick={wipe}
              className="flex items-center gap-1 text-xs text-red-600 underline"><Trash size={12} /> {t("pre.wipe", "Wipe learned data")}</button>
          )}
          {st.status === "error" && <span className="text-xs text-red-600">{t("pre.failed", "Last run failed:")} {st.error}</span>}
        </div>
      )}
      {p && (
        <div data-testid="prelearn-style-summary" className="mt-3 bg-purple-50 border border-purple-100 rounded-md p-3 text-xs text-purple-900">
          <span className="font-semibold block mb-0.5">{t("pre.style_title", "My writing style (learned from {n} sent emails)", { n: st.tone_profile.analyzed })}</span>
          {t("pre.style_line", "Greeting: \"{greeting}\" · Sign-off: \"{signoff}\" · Formality {formality}/5 · {n} emails · language: {lang}", { greeting: p.greeting_style, signoff: p.signoff, formality: p.formality, n: p.avg_length, lang: p.language })}
          {p.notes && <span className="block mt-0.5 text-purple-700">{p.notes}</span>}
        </div>
      )}
    </div>
  );
};
